import { useState, useEffect } from 'react';
import { useAccounts } from '../../../hooks/useAccounts';
import { CurrencyInput } from '../../../components/base/CurrencyInput';
import type { Card, CardStats } from '../../../hooks/useCards';

export type PaymentType = 'full' | 'partial';

export interface CardPaymentParams {
  cardId: string;
  amount: number;
  paymentDate: string;
  paymentMethod: string;
  sourceAccountId: string | null;
  notes: string;
  affectsAccountBalance: boolean;
  billingCycleStart?: string;
}

interface CardPaymentModalProps {
  open: boolean;
  card: Card | null;
  stats: CardStats | null;
  initialType?: PaymentType;
  onClose: () => void;
  onConfirm: (params: CardPaymentParams) => Promise<{ success: boolean; message: string }>;
}

const PAYMENT_METHODS = [
  { value: 'pix', label: 'Pix', icon: 'ri-qr-code-line' },
  { value: 'ted', label: 'TED / Transferência', icon: 'ri-exchange-dollar-line' },
  { value: 'boleto', label: 'Boleto', icon: 'ri-barcode-line' },
  { value: 'conta', label: 'Débito em conta', icon: 'ri-bank-line' },
  { value: 'dinheiro', label: 'Dinheiro', icon: 'ri-money-dollar-circle-line' },
  { value: 'debito_automatico', label: 'Débito automático', icon: 'ri-loop-right-line' },
];

const fmt = (val: number) =>
  val.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const todayStr = () => new Date().toISOString().split('T')[0];

export const CardPaymentModal = ({
  open, card, stats, initialType = 'full', onClose, onConfirm,
}: CardPaymentModalProps) => {
  const { accounts } = useAccounts();
  const [payType, setPayType] = useState<PaymentType>(initialType);
  const [amount, setAmount] = useState<number>(0);
  const [paymentDate, setPaymentDate] = useState(todayStr());
  const [paymentMethod, setPaymentMethod] = useState('pix');
  const [sourceAccountId, setSourceAccountId] = useState('');
  const [notes, setNotes] = useState('');
  const [affectsBalance, setAffectsBalance] = useState(true);
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);

  const invoiceAmount = stats?.currentInvoice ?? 0;
  const pendingAmount = stats?.pendingAmount ?? Math.max(0, invoiceAmount - (stats?.totalPaid ?? 0));

  useEffect(() => {
    if (open) {
      setPayType(initialType);
      setAmount(initialType === 'full' ? pendingAmount : 0);
      setPaymentDate(todayStr());
      setPaymentMethod('pix');
      setSourceAccountId(card?.linked_account_id ?? '');
      setNotes('');
      setAffectsBalance(true);
      setFeedback(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialType]);

  useEffect(() => {
    if (payType === 'full') {
      setAmount(pendingAmount);
    }
  }, [payType, pendingAmount]);

  // Show account selector when method requires it
  const needsAccount = paymentMethod === 'conta' || paymentMethod === 'debito_automatico';

  useEffect(() => {
    if (!needsAccount) {
      setAffectsBalance(true);
    }
  }, [needsAccount]);

  const handleSubmit = async () => {
    if (!amount || amount <= 0) {
      setFeedback({ type: 'error', msg: 'Informe um valor válido.' });
      return;
    }
    if (payType === 'partial' && amount > pendingAmount && pendingAmount > 0) {
      setFeedback({ type: 'error', msg: `O valor não pode exceder o pendente de ${fmt(pendingAmount)}.` });
      return;
    }
    if (affectsBalance && !sourceAccountId) {
      setFeedback({ type: 'error', msg: 'Selecione a conta de origem para registrar a saída.' });
      return;
    }
    if (!card) return;

    setLoading(true);
    setFeedback(null);
    const result = await onConfirm({
      cardId: card.id,
      amount,
      paymentDate,
      paymentMethod,
      sourceAccountId: (affectsBalance && sourceAccountId) ? sourceAccountId : null,
      notes,
      affectsAccountBalance: affectsBalance && !!sourceAccountId,
      billingCycleStart: stats?.currentCycleStart,
    });
    setLoading(false);
    if (result.success) {
      setFeedback({ type: 'success', msg: result.message });
      setTimeout(() => onClose(), 1200);
    } else {
      setFeedback({ type: 'error', msg: result.message });
    }
  };

  if (!open || !card) return null;

  const activeAccounts = accounts.filter(a => !a.is_archived);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-md shadow-xl overflow-hidden">
        {/* Header */}
        <div
          className="px-5 pt-4 pb-4 text-white"
          style={{ background: `linear-gradient(135deg, ${card.color}, ${card.color}cc)` }}
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                <i className="ri-bank-card-line text-white text-sm" />
              </div>
              <div>
                <p className="text-white/70 text-[11px]">Pagamento de fatura</p>
                <p className="text-white text-[15px] font-700">{card.name}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-7 h-7 flex items-center justify-center rounded-lg bg-white/20 hover:bg-white/40 text-white cursor-pointer"
            >
              <i className="ri-close-line text-sm" />
            </button>
          </div>
          {/* Invoice info */}
          <div className="flex items-center gap-3 mt-1">
            <div className="bg-white/15 rounded-xl px-3 py-2 flex-1">
              <p className="text-white/70 text-[10px] font-500">Fatura total</p>
              <p className="text-white text-[16px] font-700">{fmt(invoiceAmount)}</p>
            </div>
            <div className="bg-white/15 rounded-xl px-3 py-2 flex-1">
              <p className="text-white/70 text-[10px] font-500">Pendente</p>
              <p className="text-white text-[16px] font-700">{fmt(pendingAmount)}</p>
            </div>
            {(stats?.totalPaid ?? 0) > 0 && (
              <div className="bg-white/15 rounded-xl px-3 py-2 flex-1">
                <p className="text-white/70 text-[10px] font-500">Já pago</p>
                <p className="text-emerald-300 text-[16px] font-700">{fmt(stats?.totalPaid ?? 0)}</p>
              </div>
            )}
          </div>
        </div>

        {/* Payment type selector */}
        <div className="px-5 pt-4 pb-3">
          <div className="flex bg-gray-100 rounded-xl p-1 gap-1">
            <button
              onClick={() => setPayType('full')}
              className={`flex-1 py-2 rounded-lg text-[12px] font-600 cursor-pointer transition-colors whitespace-nowrap ${
                payType === 'full' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'
              }`}
            >
              <i className="ri-check-double-line mr-1" />
              Pagar valor total
            </button>
            <button
              onClick={() => setPayType('partial')}
              className={`flex-1 py-2 rounded-lg text-[12px] font-600 cursor-pointer transition-colors whitespace-nowrap ${
                payType === 'partial' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'
              }`}
            >
              <i className="ri-percent-line mr-1" />
              Pagamento parcial
            </button>
          </div>
        </div>

        {/* Form */}
        <div className="px-5 pb-4 space-y-3 max-h-[50vh] overflow-y-auto">
          {/* Amount */}
          <div>
            <label className="text-[12px] font-600 text-gray-600 block mb-1.5">
              {payType === 'full' ? 'Valor a pagar (pendente)' : 'Valor pago parcialmente'}
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[13px] text-gray-400 font-600 z-10 pointer-events-none">R$</span>
              <CurrencyInput
                value={amount}
                onChange={(v) => setAmount(v)}
                readOnly={payType === 'full'}
                placeholder="0,00"
                className={`w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-xl text-[14px] font-600 text-gray-900 focus:outline-none focus:border-emerald-400 ${
                  payType === 'full' ? 'bg-gray-50 text-gray-500 cursor-default' : 'bg-white'
                }`}
              />
            </div>
            {payType === 'partial' && (
              <p className="text-[11px] text-gray-400 mt-1">
                Máximo: {fmt(pendingAmount)} · Limite liberado proporcionalmente
              </p>
            )}
          </div>

          {/* Payment date */}
          <div>
            <label className="text-[12px] font-600 text-gray-600 block mb-1.5">
              Data do pagamento
            </label>
            <input
              type="date"
              value={paymentDate}
              onChange={e => setPaymentDate(e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-[13px] text-gray-800 focus:outline-none focus:border-emerald-400 bg-white"
            />
          </div>

          {/* Payment method */}
          <div>
            <label className="text-[12px] font-600 text-gray-600 block mb-1.5">
              Forma de pagamento
            </label>
            <div className="grid grid-cols-2 gap-1.5">
              {PAYMENT_METHODS.map(m => (
                <button
                  key={m.value}
                  onClick={() => setPaymentMethod(m.value)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-[11px] font-500 cursor-pointer transition-colors whitespace-nowrap ${
                    paymentMethod === m.value
                      ? 'border-emerald-400 bg-emerald-50 text-emerald-700'
                      : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <i className={`${m.icon} text-[13px]`} />
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          {/* Source account (shown for all methods when user wants to affect balance) */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <input
                type="checkbox"
                id="affects-balance"
                checked={affectsBalance}
                onChange={e => setAffectsBalance(e.target.checked)}
                className="w-4 h-4 accent-emerald-500 cursor-pointer"
              />
              <label htmlFor="affects-balance" className="text-[12px] font-600 text-gray-700 cursor-pointer">
                Registrar saída na conta bancária
              </label>
            </div>
            {affectsBalance && (
              <select
                value={sourceAccountId}
                onChange={e => setSourceAccountId(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-[13px] text-gray-800 focus:outline-none focus:border-emerald-400 bg-white"
              >
                <option value="">Selecionar conta de origem</option>
                {activeAccounts.map(a => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
            )}
            {affectsBalance && (
              <p className="text-[11px] text-gray-400 mt-1">
                Uma despesa será registrada na conta selecionada.
              </p>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className="text-[12px] font-600 text-gray-600 block mb-1.5">
              Observação <span className="font-400">(opcional)</span>
            </label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              maxLength={300}
              rows={2}
              placeholder="Ex: pagamento via app do banco..."
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-[13px] text-gray-800 resize-none focus:outline-none focus:border-emerald-400"
            />
          </div>

          {/* Feedback */}
          {feedback && (
            <div
              className={`px-3 py-2.5 rounded-xl text-[12px] font-500 ${
                feedback.type === 'success' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-600 border border-red-200'
              }`}
            >
              <i className={`${feedback.type === 'success' ? 'ri-check-line' : 'ri-error-warning-line'} mr-1.5`} />
              {feedback.msg}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-100 flex items-center gap-3">
          <button
            onClick={onClose}
            disabled={loading}
            className="flex-1 py-2.5 border border-gray-200 text-gray-600 font-600 rounded-xl text-[13px] cursor-pointer hover:bg-gray-50 disabled:opacity-50 whitespace-nowrap"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="flex-1 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white font-600 rounded-xl text-[13px] cursor-pointer disabled:opacity-50 whitespace-nowrap flex items-center justify-center gap-2"
          >
            {loading ? (
              <i className="ri-loader-4-line animate-spin" />
            ) : (
              <i className="ri-check-line" />
            )}
            {loading ? 'Registrando...' : 'Confirmar pagamento'}
          </button>
        </div>
      </div>
    </div>
  );
};
